import { useEffect, useState } from 'react';
import { Typography, Box } from '@mui/material';
import {
  ArrayInput,
  FormDataConsumer,
  Labeled,
  NumberInput,
  ReferenceInput,
  SelectInput,
  SimpleFormIterator,
  minLength,
  minValue,
  required,
  useDataProvider,
  useInput,
  useNotify,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { AddCustomItemButton, PriceInput } from '../../../components';
import { BusinessModel, ContractProductMeasureType, DealValueType } from '../../../models';
import { getChainId } from '../../../utils/utils';

const FormContractFuels = ({ fuels, isEdit = false }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const companyInput = useInput({ source: 'companyId' });
  const { getValues, setValue } = useFormContext();
  const [businessModel, setBusinessmodel] = useState(null);

  useEffect(() => {
    if (businessModel !== BusinessModel.pumpxPro) {
      const contractFuels = getValues('contractFuels') || [];

      const updated = contractFuels.map(item => {
        if (item.dealValueType === DealValueType.prefixed) {
          return { ...item, dealValueType: null };
        }
        return item;
      });

      const hasChange = contractFuels.some((item, i) => item.dealValueType !== updated[i].dealValueType);
      if (hasChange) {
        setValue('contractFuels', updated);
      }
    }
  }, [businessModel, getValues, setValue]);

  useEffect(() => {
    const chainId = getValues('chainId') || getChainId();
    if (!companyInput.field.value || !chainId) { return; }

    dataProvider.getList('companies', { filter: { companyId: companyInput.field.value, chainId } })
      .then(({ data }) => {
        setBusinessmodel(data?.[0]?.chainCompany?.[0]?.businessModel ?? null);
      })
      .catch(() => {
        notify('Erro ao buscar informações sobre a transportadora. Recarregue a página', { type: 'error' });
      });
  }, [companyInput.field.value]);

  return (
    <ArrayInput
      source="contractFuels"
      label=""
      sx={{ marginBottom: 4 }}
      validate={isEdit ? [] : [minLength(1), required()]}
    >
      <SimpleFormIterator
        disableReordering
        fullWidth
        sx={{ '& .RaSimpleFormIterator-action': { visibility: 'visible' } }}
        addButton={isEdit ? <AddCustomItemButton name="contractFuels" resetFields={{ dealType: "discount", dealValue: 0, fuelId: "", dealValueType: "", dealMinValue: 0 }} /> : undefined}
      >
        <FormDataConsumer>
          {({ scopedFormData: scopedFormDataContractFuel, formData, getSource }) => {
            return (
              <>
                <Box sx={{ marginTop: 2 }}>
                  <SelectInput
                    source={getSource('fuelId')}
                    choices={(fuels || []).filter(fuel => fuel.acronym !== 'ARL')}
                    label="Combustível"
                    sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                    record={scopedFormDataContractFuel}
                    optionText="name"
                    style={{ minWidth: 220 }}
                    validate={required()}
                  />
                  <SelectInput
                    label="Tipo"
                    sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                    source={getSource('dealValueType')}
                    record={scopedFormDataContractFuel}
                    choices={DealValueType.dealValueTypeChoices}
                    isRequired
                  />
                  {scopedFormDataContractFuel && (scopedFormDataContractFuel.dealValueType === 'cents' || scopedFormDataContractFuel.dealValueType === 'prefixed')
                    ? <PriceInput
                      sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                      defaultValue={0}
                      source={getSource('dealValue')}
                      label="Valor"
                      record={scopedFormDataContractFuel}
                    />
                    : <NumberInput
                      sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                      defaultValue={0}
                      source={getSource('dealValue')}
                      label="Valor"
                      record={scopedFormDataContractFuel}
                    />
                  }
                  <NumberInput
                    sx={{ width: 200, marginTop: 2 }}
                    source={getSource('dealMinValue')}
                    defaultValue={0}
                    label="Mínimo para promoção (litros)"
                    record={scopedFormDataContractFuel}
                    min={0}
                    validate={[minValue(0)]}
                  />
                </Box>
                {scopedFormDataContractFuel && (scopedFormDataContractFuel.dealValueType && scopedFormDataContractFuel.dealValueType !== 'prefixed') &&
                  <SelectInput
                    source={getSource('dealType')}
                    record={scopedFormDataContractFuel}
                    label="Desconto ou Acréscimo?"
                    defaultValue="discount"
                    sx={{ marginRight: 2, minWidth: 200 }}
                    choices={[
                      { id: 'discount', name: 'Desconto' },
                      { id: 'increase', name: 'Acréscimo' },
                    ]}
                    isRequired
                  />
                }
              </>
            )
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
};

export const DealsStep = ({ fuels, isEdit = false }) => {
  return (
    <>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        Acordos
      </Typography>

      <Labeled label="Acordos de Preço">
        <FormContractFuels fuels={fuels} isEdit={isEdit} />
      </Labeled>

      <Labeled label="Acordos de Produtos">
        <ArrayInput source="contractProducts" label="" style={{ marginBottom: 40 }}>
          <SimpleFormIterator
            disableReordering
            fullWidth
            sx={{ '& .RaSimpleFormIterator-action': { visibility: 'visible' } }}
            addButton={<AddCustomItemButton name="contractProducts" resetFields={{ dealType: "discount", dealValue: 0, fuelId: "", dealValueType: null, dealMinValue: 0 }} />}
          >
            <FormDataConsumer>
              {({ scopedFormData: scopedFormDataContractProduct, formData, getSource }) => {
                return (
                  <>
                    <Box sx={{ marginTop: 2 }}>
                      <ReferenceInput
                        reference="fuels"
                        source={getSource('fuelId')}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{ acronym: 'ARL' }}
                        perPage={false}
                      >
                        <SelectInput
                          label="Produto"
                          sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                          record={scopedFormDataContractProduct}
                          optionText="name"
                          validate={required()}
                        />
                      </ReferenceInput>
                      <SelectInput
                        source={getSource('measureType')}
                        label="Medida"
                        choices={ContractProductMeasureType.contractProductMeasureTypeChoices}
                        sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                        record={scopedFormDataContractProduct}
                        optionText="name"
                        style={{ minWidth: 150 }}
                        validate={required()}
                      />
                      {
                        (scopedFormDataContractProduct && (scopedFormDataContractProduct.measureType === ContractProductMeasureType.inBulk)) &&
                        <>
                          <SelectInput
                            label="Tipo de valor"
                            sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                            source={getSource('dealValueType')}
                            record={scopedFormDataContractProduct}
                            defaultValue="prefixed"
                            choices={[
                              { id: 'prefixed', name: 'Prefixado' },
                            ]}
                            isRequired
                          />
                          {scopedFormDataContractProduct && (scopedFormDataContractProduct.dealValueType === 'prefixed') &&
                            <PriceInput
                              sx={{ width: 150, marginRight: 2, marginTop: 2 }}
                              defaultValue={0}
                              source={getSource('dealValue')}
                              label="Valor"
                              record={scopedFormDataContractProduct}
                            />
                          }
                          <NumberInput
                            sx={{ width: 200, marginTop: 2 }}
                            source={getSource('dealMinValue')}
                            defaultValue={0}
                            label="Mínimo para promoção (litros)"
                            record={scopedFormDataContractProduct}
                            min={0}
                            validate={[minValue(0)]}
                          />
                        </>
                      }
                      {
                        (scopedFormDataContractProduct && (scopedFormDataContractProduct.measureType === ContractProductMeasureType.gallon)) &&
                        <>
                          <SelectInput
                            source={getSource('amount')}
                            label="Capacidade"
                            choices={[
                              { id: 5, name: '5 litros' },
                              { id: 10, name: '10 litros' },
                              { id: 20, name: '20 litros' },
                            ]}
                            record={scopedFormDataContractProduct}
                            sx={{ width: 100, marginRight: 2, marginTop: 2 }}
                            validate={[required()]}
                          />
                          <PriceInput
                            sx={{ width: 150, marginRight: 2, marginTop: 2 }}
                            defaultValue={0}
                            source={getSource('dealValue')}
                            label="Valor"
                            record={scopedFormDataContractProduct}
                          />
                        </>
                      }
                    </Box>
                    {scopedFormDataContractProduct && (scopedFormDataContractProduct.dealValueType && scopedFormDataContractProduct.dealValueType !== 'prefixed') &&
                      <SelectInput
                        label="Desconto ou acrescimo?"
                        source={getSource('dealType')}
                        choices={[
                          { id: 'discount', name: 'Desconto' },
                          { id: 'increase', name: 'Acréscimo' },
                        ]}
                        sx={{ width: 100, marginRight: 2 }}
                        isRequired
                      />
                    }
                  </>
                )
              }}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      </Labeled>
    </>
  );
};
